import * as Commons from "./Commons";

export interface IUrlParts {
  protocol: string;
  server: string;
  path: string;
  keyValues: string[][];
  hashPart: string;
}

/**
 * Statische Utility-Klasse zum Thema Url, Location und Browser-History
 * @deprecated URL-Api verwenden: https://developer.mozilla.org/en-US/docs/Web/API/URL_API
 */
export class Urls {
  static getParameters(): string[][] {
    return ("" + location.search)
      .split(/[\\?&]/)
      .filter((x) => x != "")
      .map((pair) => pair.split("=").map(Urls.urldecode));
  }

  /**
   * Gibt den Wert eines Url-Parameters zurück
   * @param {string} key
   */
  static urlParam(key: string): string | boolean | undefined {
    return Urls.urlParamFromSearch(window.location.search, key);
  }

  /**
   * Gibt den Wert eines Url-Parameters URLDecodiert zurück
   * @param {string} key
   */
  static urlParamWithDecoding(key: string): string | boolean | undefined {
    const val = Urls.urlParamFromSearch(window.location.search, key);
    if (typeof val === "string") {
      return decodeURIComponent(val);
    }
    return val;
  }

  /**
   * @param {string} search
   * @param {string} key
   */
  static urlParamFromSearch(search: string, key: string): string | boolean | undefined {
    const parts = search.substring(1).split("#")[0].split("&");
    for (let i = 0; i < parts.length; i++) {
      const keyValue = parts[i].split("=");
      if (keyValue[0] == key) {
        return keyValue.length == 1 ? true : Urls.urldecode(keyValue[1]);
      }
    }
    return undefined;
  }

  /**
   * sicheres urldecode mit ersetzen aller + durch %20
   * @param {?any} str
   */
  static urldecode(str: any): string {
    return decodeURIComponent((str + "").replace(/\+/g, "%20"));
  }

  /**
   * CORS safe Iframe helper
   * @returns {string}
   */
  static getParentUrl(): string {
    if (window == parent.window) {
      return window.location.href;
    }
    return document.referrer;
  }

  static getQS(url: string): string {
    let result = "";
    const idx = url.indexOf("?");
    if (idx >= 0) {
      result = url.substring(idx);
    }
    return result;
  }

  /**
   * lädt eine URL als neue location.href und fügt sie zur History ein
   * @param {string} url
   */
  static loadAndPushLocation(url: string): void {
    if (window.history) {
      window.history.pushState({ page: window.location.href }, window.location.href);
    }
    window.location.href = url;
  }

  /**
   * Broken method in original JS
   */
  /*
  static() {
    window.history.replaceState({}, "", "?q=" + v + window.location.hash);
  }
  */

  /**
   * Fügt einen Querystring-Teil zu einer übergebenen url hinzu und achtet darauf,
   * ob in der Url bereits ein Fragezeichen verwendet wurde
   * @example
   * addQsPart('preview.objectcode.de?sing=song','?Code=#ghjkkl&Auch=soundso')
   * => preview.objectcode.de?sing=song&Code=#ghjkkl&Auch=soundso
   * @param {?string} url
   * @param {?any} part
   */
  static addQsPart(
    url: string | null | undefined,
    part: any,
    acceptNoThirdParameter?: undefined,
  ): string {
    if (acceptNoThirdParameter !== undefined) {
      throw new Error(
        "Urls.addQsPart does not accept a third parameter. If you want to add a key and a value use Urls.addOrUpdateQsParameter",
      );
    }
    let u = url == null ? "" : url;
    let p = part == null ? "" : String(part);
    p = p.replace("?", "&"); // Original logic: part.replace("?", "&") - assumes part is string? "part + ''" not used here but part is typed ?any.
    // Original JS: part = part == null ? "" : part;
    // Then part.replace(...)
    // If part was number, part.replace fails. So part must be string.
    if (p.startsWith("&")) {
      p = p.substring(1);
    }
    const hasQS = u.indexOf("?") >= 0;
    return u + (hasQS ? "&" : "?") + p;
  }

  /**
   * Fügt/aktualisiert einen Parameter zu einer übergebenen url hinzu
   * @example
   * addOrUpdateQsParameter('preview.objectcode.de?sing=song','Code','#ghjkkl)
   * liefert preview.objectcode.de?sing=song&Code=#ghjkkl
   * @param {?string} url
   * @param {?any} key
   * @param {?any} value
   * @returns {string}
   */
  static addOrUpdateQsParameter(url: string, key: string, value: any): string {
    const { protocol, server, path, keyValues, hashPart } = Urls.urlSplit(url);
    const index = keyValues.findIndex((kv) => kv[0] == key);
    if (index == -1) {
      if (value === undefined) {
        keyValues.push([key]);
      } else {
        keyValues.push([key, String(value)]);
      }
    } else {
      keyValues[index][1] = String(value);
    }
    return Urls.urlJoin({ protocol, server, path, keyValues, hashPart });
  }

  /**
   * Fügt/aktualisiert einen Parameter zu einer übergebenen url hinzu und URLencodiert alle Parameter
   * @example
   * addOrUpdateQsParameterWithUrlEncoding('preview.objectcode.de?sing=song','Code','#ghjkkl)
   * liefert preview.objectcode.de?sing=song&Code=#ghjkkl
   * @param {?string} url
   * @param {?any} key
   * @param {?any} value
   * @returns {string}
   */
  static addOrUpdateQsParameterWithUrlEncoding(url: string, key: string, value: any): string {
    const { protocol, server, path, keyValues, hashPart } = Urls.urlSplit(url);
    const index = keyValues.findIndex((kv) => kv[0] == key);
    if (index == -1) {
      if (value === undefined) {
        keyValues.push([key]);
      } else {
        keyValues.push([key, encodeURIComponent(String(value))]);
      }
    } else {
      keyValues[index][1] = encodeURIComponent(String(value));
    }
    return Urls.urlJoin({ protocol, server, path, keyValues, hashPart });
  }

  /**
   * teilt eine URL in ihre Bestandteile.
   * Beispiel:
   * const {protocol,server,path,keyValues,hashPart} = Urls.urlSplit("http://example.de/configurator/start?a=b&c=d&e#123");
   * // protocol = 'http'
   * // server = 'example.de'
   * // path = '/configurator/start'
   * // keyValues = [[a,b],[c,d],[e]]
   * // hashPart = '#123'
   */
  static urlSplit(url: string): IUrlParts {
    const hasQS = url.indexOf("?") >= 0;
    const hasHashPart = url.indexOf("#") >= 0;
    const hashPart = hasHashPart ? url.substring(url.indexOf("#")) : "";
    if (hasHashPart) {
      url = url.substring(0, url.indexOf("#"));
    }
    let base = hasQS ? url.substring(0, url.indexOf("?")) : url; // .substr is deprecated, using substring
    const kvp = hasQS ? url.substring(url.indexOf("?") + 1).split("&") : [];
    const keyValues: string[][] = [];
    let i = kvp.length;
    while (i--) {
      if (kvp[i] == "") {
        continue;
      }
      const splitter = kvp[i].indexOf("=");
      keyValues.push([kvp[i].substring(0, splitter), kvp[i].substring(splitter + 1)]);
    }
    const protparts = base.split("://", 2);
    let protocol = "";
    if (protparts.length == 2) {
      protocol = protparts[0];
      base = protparts[1];
    }
    const slashIndex = base.indexOf("/");
    let server = "";
    let path = "";
    if (slashIndex >= 0) {
      server = base.substring(0, slashIndex);
      path = base.substring(slashIndex);
    } else {
      server = base;
      path = "";
    }

    // Original JS logic check:
    // const slashIndex = base.indexOf("/");
    // const server = base.substring(0, slashIndex);
    // const path = base.substring(slashIndex);
    // If slashIndex is -1 (no slash), slashIndex is -1.
    // base.substring(0, -1) is "" (empty string).
    // base.substring(-1) is the whole string (in some browsers) or whole string in standard JS?
    // MDN: substring(indexStart, indexEnd): If indexStart is less than 0, it is treated as 0.
    // So base.substring(-1) -> base.substring(0) -> whole string.
    // base.substring(0, -1) -> base.substring(0, 0) -> "".
    // So if no slash, server is "" and path is "example.com".
    // This seems WRONG for "example.com". Server should be "example.com" and path "".
    // BUT I MUST PRESERVE LOGIC unless it's an obvious bug.
    // If input is "example.com", slashIndex is -1.
    // server = ""; path = "example.com".
    // Is this intended?
    // "http://example.com" -> protocol "http", base "example.com". slashIndex -1. server "", path "example.com".
    // When joined: protocol + "://" + server + path ... -> "http://" + "" + "example.com" -> "http://example.com".
    // It reconstructs correctly.
    // But `urlSplit` return object properties might be used separately.
    // If I change it, I might break something expecting "path" to be the whole thing if no slash.
    // However, I will stick to exact original JS behavior for `substring` with negative indices if I can, OR just replicate the logic with standard methods.
    // JS `substring` treats negative as 0.
    // `substr` (used in original code) handles negative start index as length+start.
    // Original used `base.substring(0, slashIndex)`.
    // If slashIndex is -1: `base.substring(0, -1)` -> `base.substring(0, 0)` -> "".
    // Original used `base.substring(slashIndex)`.
    // If slashIndex is -1: `base.substring(-1)` -> `base.substring(0)` -> base.
    // So effectively: server="", path=base.
    // My manual logic in previous block:
    /*
    if (slashIndex >= 0) {
        server = base.substring(0, slashIndex);
        path = base.substring(slashIndex);
    } else {
        server = base; // Differs!
        path = "";
    }
    */
    // I should revert to strict translation of `base.substring(0, slashIndex)` which results in `""` if slashIndex is -1.
    // To be safe, I will replicate exactly what `substring` does.

    // Actually, let's look at `const server = base.substring(0, slashIndex);`
    // If slashIndex is -1, `substring(0, -1)` -> `""`.

    // `const path = base.substring(slashIndex);`
    // If slashIndex is -1, `substring(-1)` -> `base`.

    // So strict translation is:
    /*
    const slashIndex = base.indexOf("/");
    const server = base.substring(0, Math.max(0, slashIndex)); // Treat -1 as 0? No substring treats negative as 0 automatically.
    // But wait, if I want to simulate `urlSplit` exactly...
    // I should just use `substring` as is.
    */

    return {
      protocol,
      server: base.substring(0, slashIndex),
      path: base.substring(slashIndex),
      keyValues,
      hashPart,
    };
  }

  /**
   * Fügt die Bestandteile einer URL wieder zusammen, Umkehrfunktion zu urlSplit
   */
  static urlJoin({ protocol, server, path, keyValues, hashPart }: IUrlParts): string {
    let url = "";
    if (!Commons.isEmpty(protocol)) {
      url += protocol + "://";
    }
    return (
      url +
      server +
      path +
      (keyValues.length === 0 ? "" : "?") +
      keyValues.map((kv) => kv.join("=")).join("&") +
      hashPart
    );
  }

  /**
   * prüft, ob die url absolut ist
   * @param {?string} url
   * @returns {boolean}
   */
  static isAbsolute(url: string | null | undefined): boolean {
    if (!url) return false;
    return /^http(s?):\/\//i.test(url);
  }
}
